import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { allBlogArticles } from '../../../mocks/blogArticles';

interface CityBlogTeaserProps {
  cityName: string;
  stateName: string;
}

const CityBlogTeaser = ({ cityName, stateName }: CityBlogTeaserProps) => {
  const navigate = useNavigate();

  const relevantArticles = useMemo(() => {
    const cityMatch = allBlogArticles.filter(
      (a) => a.city.toLowerCase() === cityName.toLowerCase()
    );
    if (cityMatch.length > 0) return cityMatch.slice(0, 3);

    const stateMatch = allBlogArticles.filter(
      (a) => a.state.toLowerCase() === stateName.toLowerCase()
    );
    if (stateMatch.length > 0) return stateMatch.slice(0, 3);

    return allBlogArticles.filter((a) => a.featured).slice(0, 3);
  }, [cityName, stateName]);

  if (relevantArticles.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Tax Tips for {cityName || 'Your Area'}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Expert strategies to maximize your take-home pay
            </p>
          </div>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); navigate('/blog'); }}
            className="hidden md:flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Articles <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {relevantArticles.map((article) => (
            <article
              key={article.id}
              onClick={() => navigate(`/blog/${article.slug}`)}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1"
            >
              <div className="relative w-full h-[180px] overflow-hidden">
                <img
                  src={article.image}
                  alt={`${article.title} - tax tips for ${article.city}`}
                  title={`${article.city} tax guide`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  width="800"
                  height="450"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <span className="inline-block px-2.5 py-0.5 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full whitespace-nowrap">
                    {article.category}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                <div className="flex items-center text-xs text-gray-400 gap-2">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-6 text-center md:hidden">
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); navigate('/blog'); }}
            className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Articles <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default CityBlogTeaser;
